import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { Delete, Edit, Image as ImageIcon } from "@mui/icons-material";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { toast } from "react-toastify";
import {
  abbreviations,
  parseContentMarkupToJSON,
  parseKeywordsMarkupToJSON,
  syntaxHighlight,
} from "./lib";
import { addWordDetail, getWordDetail } from "../../store/study/studyBaseApi";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { toast as toastFn } from "react-toastify";
import { imageFileFilter } from "../../utils";
import { useParams } from "react-router-dom";
import { uploadImage } from "../../store/questionBank/questionBankBaseApi";
import { CsvEditor } from "../../pages/diagnostic/CsvEditor";

const useStyles = makeStyles()((theme) => ({
  editorContainer: {
    padding: theme.spacing(2),
    maxWidth: "100%",
    margin: "auto",
    height: "100%",
  },
  textArea: {
    marginBottom: theme.spacing(2),
  },
  previewContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#f4f4f4",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minHeight: "275px",
    overflowY: "auto",
  },
  dialogTextField: {
    marginTop: theme.spacing(2),
  },
  previewItem: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: "grab",
    display: "flex",
    justifyContent: "space-between",
  },
  imagePlaceholder: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    backgroundColor: "#e0f7fa",
    color: "#00796b",
    borderRadius: 4,
    fontSize: 12,
    marginTop: theme.spacing(1),
    // marginLeft: theme.spacing(2),
    cursor: "pointer",
  },
  previewContainerInner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  imageContainer: {
    // height: '400px',
    // width: '500px',
    // overflow: 'hidden',
    // display: 'flex',
    // justifyContent: 'center',
    // alignItems: 'center',
    width: "100%",
    height: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  mediaElement: {
    width: "100%",
    height: "auto",
    maxWidth: "100%",
    objectFit: "contain",
    overflow: "hidden",
  },
  imageLoadingContainer: {
    // height: 'auto',
    width: "100%",
    textAlign: "center",
  },
  jsonViewer: {
    fontSize: "14px",
    whiteSpace: "break-spaces",
    "& .json-key": { color: "brown" },
    "& .json-string": { color: "green" },
    "& .json-number": { color: "darkorange" },
    "& .json-boolean": { color: "blue" },
    "& .json-null": { color: "gray" },
  },
  highlighter: {
    fontWeight: "700",
    background: "#FFC74A33",
    border: "1px solid #FFC74A",
    padding: "6px",
    borderRadius: "8px",
  },
}));

const LINE_SEPARATOR = ".";

export function MarkupEditor({
  devMode = false,
  irLevels = [],
  onSave,
  keyWords = "",
  content = "",
  disabled = false,
  noExtract = false,
  isAssessment = false,
}) {
  const { classes } = useStyles();
  const { id } = useParams();
  const [editorData, setEditorData] = useState("");
  const [parsedContent, setParsedContent] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [selectionText, setSelectionText] = useState("");
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [keywordDiaglogOpen, setKeywordDialogOpen] = useState(false);
  const [synonyms, setSynonyms] = useState("");
  const [definition, setDefinition] = useState("");
  const [selectedLevels, setSelectedlevels] = useState([]);
  const [wordDetailLoading, setWordDetailLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const selectionRef = useRef(null);
  const higlightedJson = useMemo(
    () => syntaxHighlight(parsedContent),
    [parsedContent],
  );
  const sentencesExist = useMemo(
    () => parsedContent.length > 0,
    [parsedContent],
  );

  const handleContextMenu = (event) => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    if (!selectedText) return;

    event.preventDefault();
    setSelectionText(selectedText);
    selectionRef.current = selection;
    setAnchorPosition({ top: event.clientY, left: event.clientX });
  };

  const handleMarkAsKeyword = () => {
    handleKeyWordDialogOpen();
    setAnchorPosition(null);
  };

  const handleContentEntry = (text = "", image = "", editIndex = -1) => {
    text = text || selectionText;

    const trimmedText = String(text).trim();
    const endsWithQuote = /["']$/.test(trimmedText);
    const needsFullStop = !trimmedText.endsWith(".");

    const finalText = needsFullStop
      ? endsWithQuote
        ? trimmedText.replace(/(["'])$/, ".$1") // Add full stop before closing quotes
        : `${trimmedText}.`
      : trimmedText;

    const newEntry = { text: finalText, image: image || null };

    // const newEntry = { text: String(text).trim(), image: image || null };
    setParsedContent((prev) => {
      // let existingInd = prev.findIndex(e => e.text === newEntry.text || e.image === image);
      // if (existingInd !== -1) {
      //     return prev.map((item, index) =>
      //         index === existingInd ? newEntry : item
      //     );
      // }
      if (editIndex !== -1) {
        // Update existing item at specific index
        return prev.map((item, index) =>
          index === editIndex ? newEntry : item,
        );
      }

      let existingInd = prev.findIndex(
        (e) => e.text === newEntry.text || e.image === image,
      );
      if (existingInd !== -1) {
        return prev.map((item, index) =>
          index === existingInd ? newEntry : item,
        );
      }

      return [...prev, newEntry];
    });
  };

  const handleKeywordDialogSubmit = () => {
    let finalSynonyms = synonyms;
    if (!synonyms || !synonyms.length) {
      finalSynonyms = [""];
    }

    const newEntry = {
      text: String(selectionText).trim(),
      synonyms: finalSynonyms,
      definition,
      levels: selectedLevels,
    };
    setKeywords((prev) => [...prev, newEntry]);
    setSelectionText("");
    setDefinition("");
    setSynonyms("");
    setKeywordDialogOpen(false);
    setSelectedlevels([]);

    // synonyms = synonyms?.filter(e => e)
    // if (!synonyms.length) {
    //     synonyms = ['default']
    // }

    addWordDetail({
      word: newEntry.text,
      synonyms,
      definition,
      levels: newEntry.levels,
    })
      .then((res) => {
        toast.success("Word added successfully");
      })
      .catch((err) => {});
  };

  const handleImagePreviewOpen = (url) => {
    setImagePreviewUrl(url);
  };

  const handleImagePreviewClose = () => {
    setImagePreviewUrl(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    const updatedContent = [...parsedContent];
    const [movedItem] = updatedContent.splice(fromIndex, 1);
    updatedContent.splice(toIndex, 0, movedItem);
    setParsedContent(updatedContent);
  };

  const handleDeleteItem = (index) => {
    setParsedContent((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeywordDelete = (keyword) => {
    if (disabled) return;
    setKeywords((prev) => prev.filter((k) => k.text !== keyword));
  };

  const parseContentToMarkup = () => {
    if (!parsedContent.length) return null;

    return parsedContent
      .map(
        (content) =>
          `[${content.text}]` + (content.image ? `{${content.image}}` : ""),
      )
      .join("\n");
  };

  const parseKeywordsToMarkup = () => {
    if (!keywords.length) return null;

    return keywords
      .map(
        (keyword) =>
          `[${keyword.text}]` +
          (keyword.definition ? `{${keyword.definition}}` : "") +
          (keyword.synonyms ? `(${keyword.synonyms})` : ""),
      )
      .join("\n");
  };

  const handleSave = () => {
    toast.success("Content saved!");
    if (onSave && typeof onSave === "function") {
      onSave(parseContentToMarkup(), parseKeywordsToMarkup());
    }
  };

  const handleKeyWordDialogOpen = () => {
    setKeywordDialogOpen(true);

    setWordDetailLoading(true);
    setDefinition("");
    setSynonyms("");
    setSelectedlevels([]);

    getWordDetail(selectionText)
      .then((res) => {
        if (res && res.data && Object.keys(res.data).length) {
          let { definition, synonyms, level } = res.data;

          setDefinition(definition);
          setSynonyms(synonyms);
          setSelectedlevels([level]);
        }
      })
      .catch(() => {})
      .finally(() => {
        setWordDetailLoading(false);
      });
  };

  const extractSentances = () => {
    // let _sentances = editorData.split(LINE_SEPARATOR).map((s) => s.trim()).filter((s) => s.length);
    // if (_sentances.length) {
    //     _sentances.forEach(sentance => handleContentEntry(sentance));
    // }

    // Split the editor data into sentences
    // let _sentences = editorData.split(LINE_SEPARATOR).map((s) => s.trim()).filter((s) => s.length);

    //previous method for sentence separation ---------------------------------
    // let _sentences = editorData
    //     .split(LINE_SEPARATOR)
    //     .map((s) => s.trim()) // Trim spaces but don't remove punctuation
    //     .filter((s) => s.length)
    //     .map((s) => {
    //         // Ensure a full stop is appended if missing
    //         if (!s.endsWith('.')) {
    //             s += '.'; // Append full stop if missing
    //         }
    //         return s;
    //     });

    // if (_sentences.length) {
    //     // Create a map of existing sentences
    //     const existingContentMap = new Map(
    //         parsedContent.map((item) => [item.text.trim(), item.image]) // Ensure consistent formatting
    //     );

    //     // Process each sentence
    //     const updatedContent = _sentences.map((sentence) => ({
    //         text: sentence,
    //         image: existingContentMap.get(sentence) || null, // Preserve the existing image
    //     }));

    //     // Update the parsed content state
    //     setParsedContent(updatedContent);
    // }

    // new logic for sentence separation
    if (!editorData.trim()) return;

    // Advanced sentence splitting function
    // const splitIntoSentences = (text) => {

    //     // Replace abbreviations with placeholders
    //     const placeholders = [];
    //     let processedText = text.replace(abbreviations, (match) => {
    //         const placeholder = `__ABBREV_${placeholders.length}__`;
    //         placeholders.push(match);
    //         return placeholder;
    //     });

    //     // Split on sentence endings followed by space and capital letter or quote
    //     const sentences = processedText
    //         .split(/(?<=[.!?])\s+(?=[A-Z"]|$)/)
    //         .map(sentence => {
    //             // Restore abbreviations
    //             let restored = sentence;
    //             placeholders.forEach((abbrev, index) => {
    //                 restored = restored.replace(`__ABBREV_${index}__`, abbrev);
    //             });
    //             return restored.trim();
    //         })
    //         .filter(s => s.length > 0);

    //     return sentences;

    // };

    // console.log('first', editorData)
    // const _sentences = splitIntoSentences(editorData).map((s) => s.trim())  // Just trim whitespace
    //     .filter(s => s.length > 0);  // Filter out empty sentences
    // // .map((s) => {
    // //     // Ensure proper punctuation
    // //     if (!s.match(/[.!?]$/)) {
    // //         s += '.';
    // //     }
    // //     return s;
    // // });

    // if (_sentences.length) {
    //     const existingContentMap = new Map(
    //         parsedContent.map((item) => [item.text.trim(), item.image])
    //     );

    //     const updatedContent = _sentences.map((sentence) => ({
    //         text: sentence,
    //         image: existingContentMap.get(sentence) || null,
    //     }));

    //     setParsedContent(updatedContent);
    // }
    const _sentences = editorData
      .split(/\n+|\r\n+|<br\s*\/?>/i) // Split on newlines or <br> tags
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (_sentences.length) {
      // Create a map of existing sentences to preserve images
      const existingContentMap = new Map(
        parsedContent.map((item) => [item.text.trim(), item.image]),
      );

      // Process each sentence
      const updatedContent = _sentences.map((sentence) => ({
        text: sentence,
        image: existingContentMap.get(sentence) || null,
      }));

      setParsedContent(updatedContent);
    }
  };

  const handleIrLevelSelectionChange = (e) => {
    setSelectedlevels(e.target.value);
  };

  useEffect(() => {
    if (!keyWords || !keyWords.length) {
      setKeywords([]);
      return;
    }

    setKeywords(parseKeywordsMarkupToJSON(keyWords));
  }, [keyWords]);

  useEffect(() => {
    if (!content || !content.length) {
      setEditorData('');
      setParsedContent([]);
      return;
    }
    const parsed = parseContentMarkupToJSON(content);
    if (parsed.length) {
      let sentances = parsed.map((e) => e.text).join("\n\n");
      setEditorData(sentances);
    }

    setParsedContent(parsed);
  }, [content]);

  const showToast = (msg, type) => {
    if (!type) {
      return toastFn(msg);
    }
    toastFn[type](msg);
  };

  const onFileChange = async (file, field) => {
    const fileSizeInMB = file.size / 1024 / 1024;
    if (!imageFileFilter(file.name)) {
      return showToast("Please upload a valid file", "error");
    } else if (fileSizeInMB > 5) {
      return showToast("Please upload file with in the 5 MB", "error");
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      setUploading(true);
      const resp = await uploadImage({ id, formData, field });
      if (resp.data && resp.data) {
        return resp?.data;
      }
      if (resp.message) {
        showToast(resp.message, "success");
      }
    } catch (e) {
      return showToast(e.error, "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className={classes.editorContainer}
        onContextMenu={handleContextMenu}
      >
        <Grid container display={"flex"} sx={{ height: "100%" }} spacing={3}>
          <Grid
            sx={{ height: "100%" }}
            item
            xs={sentencesExist ? 12 : 12}
            md={!noExtract && sentencesExist ? 6 : 12}
          >
            <h3>Textual Paragraph</h3>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              rows={12}
              placeholder="Type or paste content here..."
              value={editorData}
              onChange={(e) => setEditorData(e.target.value)}
              className={classes.textArea}
              disabled={disabled}
            />
            {!isAssessment && !noExtract && !disabled && (
              <Button variant="outlined" onClick={extractSentances}>
                Extract Sentences
              </Button>
            )}
          </Grid>

          {!noExtract && sentencesExist && (
            <Grid sx={{ height: "100%" }} item xs={12} md={6}>
              <h3>Sentences</h3>
              <div className={classes.previewContainer}>
                {parsedContent.map((item, index) => (
                  <DraggableItem
                    key={`${index}-${keywords.length}`}
                    text={item.text}
                    image={item.image}
                    index={index}
                    moveItem={moveItem}
                    onDelete={handleDeleteItem}
                    onEdit={handleContentEntry}
                    onImageClick={handleImagePreviewOpen}
                    keywords={keywords}
                    disabled={disabled}
                    onFileChange={onFileChange}
                    uploading={uploading}
                  />
                ))}
              </div>
            </Grid>
          )}
        </Grid>
        <Grid sx={{ mt: 4 }}>
          {!isAssessment && keywords.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <h2>Keywords</h2>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {keywords.map((keyword) => {
                  let title = "";
                  if (keyword.definition) {
                    title += `Definition: ${keyword.definition}. `;
                  }
                  if (keyword.synonyms) {
                    title += `Synonyms: ${keyword.synonyms}`;
                  }
                  return (
                    <Tooltip
                      key={keyword.text}
                      title={title}
                      arrow
                      placement="top-end"
                    >
                      <Chip
                        key={keyword.text}
                        label={keyword.text}
                        onDelete={
                          disabled
                            ? null
                            : () => handleKeywordDelete(keyword.text)
                        }
                        deleteIcon={!disabled ? <Cancel /> : null}
                        sx={{
                          "& .MuiChip-deleteIcon": {
                            color: "gray", // Adjust color if needed
                            "&:hover": {
                              color: "red", // Color on hover
                            },
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}
          {devMode && (
            <Box>
              <h2>Markup Content</h2>
              <pre
                className={classes.jsonViewer}
                dangerouslySetInnerHTML={{ __html: higlightedJson }}
              />
            </Box>
          )}

          {!disabled && (
            <Box>
              <Button variant="contained" onClick={handleSave}>
                Save content
              </Button>
              <span
                style={{ marginLeft: "20px", color: "grey", fontSize: "0.8em" }}
              >
                ( Click "
                <span style={{ color: "#000", fontWeight: "600" }}>
                  Save Content
                </span>
                " to save your changes. )
              </span>
            </Box>
          )}
        </Grid>

        {!disabled && (
          <Menu
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition}
            open={Boolean(anchorPosition)}
            onClose={() => setAnchorPosition(null)}
          >
            <MenuItem onClick={handleMarkAsKeyword}>Add to Keywords</MenuItem>
          </Menu>
        )}

        <Dialog
          open={keywordDiaglogOpen}
          onClose={() => setKeywordDialogOpen(false)}
        >
          <DialogTitle>
            <Grid container justifyContent={"space-between"}>
              <Grid item>Add to Keywords</Grid>
              {wordDetailLoading && (
                <Grid item>
                  <CircularProgress size={20} />
                </Grid>
              )}
            </Grid>
          </DialogTitle>
          <DialogContent>
            <p>Selected keyword:</p>
            <TextField
              fullWidth
              variant="outlined"
              value={selectionText}
              disabled
            />
            <TextField
              fullWidth
              label={
                <span>
                  Definition <span style={{ color: "red" }}>*</span>
                </span>
              }
              variant="outlined"
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              className={classes.dialogTextField}
              disabled={wordDetailLoading}
            />
            <TextField
              fullWidth
              label={<span>Synonyms </span>}
              variant="outlined"
              value={synonyms}
              onChange={(e) => setSynonyms(e.target.value)}
              className={classes.dialogTextField}
              disabled={wordDetailLoading}
            />
            <FormControl className={classes.dialogTextField} fullWidth>
              <InputLabel id="multi-select-label">Select Level(s)</InputLabel>
              <Select
                labelId="multi-select-label"
                multiple
                value={selectedLevels}
                onChange={handleIrLevelSelectionChange}
                renderValue={(selected) => selected.join(", ")}
              >
                {(irLevels || []).map((option) => (
                  <MenuItem key={option} value={option}>
                    <Checkbox checked={selectedLevels.indexOf(option) > -1} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ py: 2, px: 3 }}>
            <Button onClick={() => setKeywordDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleKeywordDialogSubmit}
              variant="contained"
              // disabled={!synonyms}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Preview Modal */}
        <Dialog
          open={Boolean(imagePreviewUrl)}
          onClose={handleImagePreviewClose}
        >
          <DialogTitle>Image Preview</DialogTitle>
          <DialogContent className={classes.imageContainer}>
            {imageLoading && (
              <Box className={classes.imageLoadingContainer}>
                <CircularProgress />
              </Box>
            )}
            {imagePreviewUrl && (
              <img
                src={imagePreviewUrl}
                alt="Preview"
                className={classes.mediaElement}
                // style={{ height: '100%' }}
                onLoad={() => setImageLoading(false)}
                onLoadStart={() => setImageLoading(true)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleImagePreviewClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </DndProvider>
  );
}

function DraggableItem({
  text,
  image,
  index,
  moveItem,
  onEdit,
  onDelete,
  onImageClick,
  keywords = [],
  disabled,
  onFileChange,
  uploading,
}) {
  const { classes } = useStyles();
  const [keywordArray, setKeywordArray] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(image);
  const [editedText, setEditedText] = useState(text);

  const mediaTypes = ["link", "upload"];
  const [selectedTab, setSelectedTab] = useState(1);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const [, ref] = useDrag({
    type: "item",
    item: { index },
  });

  const [, drop] = useDrop({
    accept: "item",
    hover(draggedItem) {
      if (disabled) return;

      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  useEffect(() => {
    setEditedText(text); // Reset the text when dialog opens
  }, [text, dialogOpen]);

  const handleEdit = () => {
    setDialogOpen(true);
  };

  const onEditingComplete = () => {
    onEdit(editedText, imageUrl, index);
    setDialogOpen(false);
  };

  useEffect(() => {
    if (keywords && keywords.length) {
      const keywordTexts = keywords.map((keyword) => keyword.text);
      setKeywordArray(keywordTexts);
    } else {
      setKeywordArray([]); // Clear highlights when no keywords
    }
  }, [keywords]);

  const handleFileUpload = async (file) => {
    const uploadedUrl = await onFileChange(file, "imageUrl");
    if (uploadedUrl) {
      setImageUrl(uploadedUrl);
    }
  };

  return (
    <React.Fragment>
      <Paper
        ref={(node) => ref(drop(node))}
        sx={{ width: "100%", mb: 2, cursor: disabled ? "pointer" : "grab" }}
      >
        <div className={classes.previewContainerInner}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={10}>
              <Box sx={{ py: 2, width: "100%" }}>
                <Typography variant="subtitle1" sx={{ pl: 2 }}>
                  {index + 1}.
                  {String(text)
                    .split(" ")
                    .map((word, idx) => {
                      let trimmed = String(word).match(/\b\w+\b/g);
                      trimmed = trimmed ? trimmed[0] : "";
                      return (
                        <Typography
                          variant="span"
                          key={word + "-" + idx}
                          sx={{ mx: 0.3, display: "inline-block" }}
                          className={
                            keywordArray.includes(trimmed)
                              ? classes.highlighter
                              : null
                          }
                        >
                          {word}
                        </Typography>
                      );
                    })}
                </Typography>
                {image && (
                  <span
                    className={classes.imagePlaceholder}
                    onClick={() => onImageClick(image)}
                  >
                    <ImageIcon fontSize="small" style={{ marginRight: 4 }} />
                    {String(image).split("/").pop()}
                  </span>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconButton
                  disabled={disabled}
                  onClick={() => handleEdit(text)}
                >
                  <Edit />
                </IconButton>

                <IconButton disabled={disabled} onClick={() => onDelete(index)}>
                  <Delete />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Paper>
      <Dialog
        open={dialogOpen}
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
          },
          width: "100%",
        }}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Mark as Sentence</DialogTitle>
        <DialogContent>
          <p>Selected Text:</p>
          <TextField
            fullWidth
            multiline
            variant="outlined"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
          />
          {/* <TextField
                        fullWidth
                        label="Image URL (optional)"
                        variant="outlined"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className={classes.dialogTextField}
                    /> */}
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {mediaTypes.map((item, index) => (
              <Tab label={item} key={index} />
            ))}
          </Tabs>
          <Grid container style={styles.gridContainer}>
            {selectedTab === 0 && (
              <Grid item xs={12}>
                <InputLabel style={styles.inputLabel}>URL</InputLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={imageUrl}
                  placeholder={"Enter Image URL (optional)"}
                  className={classes.dialogTextField}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </Grid>
            )}
            {selectedTab === 1 && (
              <Grid item xs={12}>
                <InputLabel style={styles.inputLabel}>Image</InputLabel>
                <span
                  style={{ color: "grey", fontSize: "0.8em", fontWeight: 500 }}
                >
                  (Upload image files in
                  <span style={{ color: "#000" }}>.jpg, .jpeg, .png, .gif</span>
                  format up to <span style={{ color: "#000" }}>5MB</span> in
                  Size)
                </span>
                <Grid container alignItems="center">
                  <Grid item xs={11}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={imageUrl}
                      className={classes.dialogTextField}
                      placeholder={"Image URL (optional)"}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {uploading && <CircularProgress size="30px" />}
                    {!uploading && (
                      <FileUploadBtn
                        onFileSelect={(file) => handleFileUpload(file)}
                        fileType={"image"}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={onEditingComplete} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

const FileUploadBtn = ({ onFileSelect, fileType }) => {
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const getAcceptedFormats = () => {
    switch (fileType) {
      case "image":
        return ".jpg, .jpeg, .png, .gif";
      case "video":
        return ".mp4, .mov, .wmv, .avi, .mkv";
      case "audio":
        return ".mp3, .wav";
      default:
        return "*"; // Allow all types if no specific type is defined
    }
  };

  return (
    <div>
      <Button
        style={styles.uploadBtn}
        variant="contained"
        onClick={handleButtonClick}
      >
        <FileUploadIcon sx={{ color: "var(--mui-palette-primary-blue)" }} />
      </Button>
      <input
        type="file"
        style={styles.hiddenInput}
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={getAcceptedFormats()}
      />
    </div>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "30px",
    height: "30px",
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: "0",
    minWidth: "30px",
  },
  hiddenInput: {
    display: "none",
  },
};
